import { css } from "@compiled/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import {
    TabGroup,
    TabsTypes,
    TabColorProps
} from "../../../../components/wpl/tab-group/src";

import TemplatesTabCardContent, {
    TemplatesTabCardContentProps
} from "./templates-tab-card";

const componentHeadingStyles = css({
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    maxWidth: "800px",
    margin: "0 auto",
    padding: "16px 0 0",
    h2: {
        fontWeight: "500",
        lineHeight: "1.33333333",
        color: "#253858",
        letterSpacing: token("space.0"),
        marginBlockStart: token("space.0"),
        textAlign: "center",
        [media.below.sm]: {
            marginBlockEnd: "14px"
        },

        [media.above.sm]: {
            fontSize: "2.25rem",
            marginBlockEnd: token("space.300")
        }
    },
    p: {
        textAlign: "center",
        margin: `0 0 ${token("space.300")}`
    }
});
const pageSectionStyles = css({
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%",
    padding: `${token("space.400")} ${token("space.200")}`,
    clear: "both",
    backgroundColor: "#e6fcff",
    position: "relative"
});

const tabContainerStyles = css({
    "display": "flex",
    "maxWidth": "1200px",
    "gap": token("space.250"),
    "margin": "0 auto",
    '[role="tablist"]': {
        gap: token("space.250"),
        fontWeight: "300",
        [media.below.md]: {
            display: "none"
        }
    },
    '[role="tablist"]::before': {
        content: ""
    },
    '[role="tab"][aria-selected="true"]': {
        backgroundColor: "#00a3bf",
        color: "#ffffff !important"
    },
    '[role="tab"][aria-selected="true"]::after': {
        content: ""
    },

    ".tab-group-inner": {
        button: {
            fontSize: "1.25rem",
            lineHeight: "1"
        }
    },

    [media.above.md]: {
        paddingBlockStart: "40px"
    },

    ".select-container": {
        [media.below.md]: {
            display: "block"
        },

        [media.above.md]: {
            display: "none"
        },

        "maxWidth": "420px",
        "margin": `${token("space.500")} auto ${token("space.0")} auto`,

        '[class*="select"]': {
            borderRadius: "0",
            fontSize: "0.9rem"
        },

        "option": {
            fontFamily: "Charlie Text",
            backgroundColor: "#ffffff !important",
            color: "#42526e !important"
        }
    }
});

const tabStyles: TabColorProps = {
    defaultBG: "n0",
    defaultText: "n300",
    hoverBG: "n0",
    hoverText: "p300",
    activeBG: "n0",
    activeText: "n300"
};

const fadeInStyles = css({
    "@keyframes fadeIn": {
        from: {
            opacity: "0"
        },
        to: {
            opacity: "1"
        }
    },
    "opacity": "0",
    "animation": "0.5s fadeIn forwards"
});

const outerGridStyles = css({
    maxWidth: "1200px",
    width: "100%",
    display: "grid",
    marginBlockStart: token("space.400"),

    [media.below.md]: {
        gridTemplateColumns: "1fr"
    },

    [media.above.md]: {
        gridTemplateColumns: "1fr 1fr"
    }
});

const innerGridStyles = css({
    display: "grid",

    [media.below.sm]: {
        gridTemplateColumns: "1fr"
    },

    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr"
    }
});

const tabLinkStyles = css({
    textAlign: "center",
    marginBlockStart: "32px"
});

const TemplatesSection = () => {
    const intl = useIntl();

    const tabOptions = [
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.featured",
            defaultMessage: "Featured"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.project-planning",
            defaultMessage: "Project Planning"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.software-development-it",
            defaultMessage: "Software Development/IT"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.product-mgmt",
            defaultMessage: "Product Mgmt."
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.section.tab.marketing-sales",
            defaultMessage: "Marketing and Sales"
        })
    ] as const;

    const translatedTemplateTabLinks = [
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.link.featured",
            defaultMessage: "See all Featured templates"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.link.project-planning",
            defaultMessage: "See all Project Planning templates"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.link.software-development-it",
            defaultMessage: "See all Software Development/IT templates"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.link.product-mgmt",
            defaultMessage: "See all Product Management templates"
        }),
        intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.link.marketing-sales",
            defaultMessage: "See all Marketing & Sales templates"
        })
    ] as const;

    const featuredCardContentFirstRow: TemplatesTabCardContentProps[] = [
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.business-status-update",
                defaultMessage: "business status update"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:c1aa5bca-94fb-4a14-be34-210dca5f6794/business-status-update.svg?cdnVersion=1275",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.business-status-update",
                defaultMessage: "Steven Bao R & D Program Manager Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.business-status-update",
                defaultMessage: "Business status update"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.business-status-update",
                defaultMessage:
                    "Provide regular updates to leadership and the wider team on business and product performance."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/business-status-update"
            )
        },
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.strategic-plan",
                defaultMessage: "strategic plan"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:63091d62-7876-405f-ae26-48ebf99f929e/business-strategy.svg?cdnVersion=1278",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.strategic-plan",
                defaultMessage: "Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.strategic-plan",
                defaultMessage: "Strategic Plan"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.strategic-plan",
                defaultMessage:
                    "Capture and present your business strategy to the executive team and board of directors."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/strategic-plan"
            )
        }
    ];
    const featuredCardContentSecondRow: TemplatesTabCardContentProps[] = [
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.vision-to-values",
                defaultMessage: "vision to values"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:af4f5943-04eb-4f6b-939a-783f3c326971/vision-to-values.svg?cdnVersion=1278",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.vision-to-values",
                defaultMessage: "Joff Redfern Head of All Teams Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "conflConfluenceSoftwarePageTemplateuence.TemplatesSection.card.title.vision-to-values",
                defaultMessage: "Vision to values "
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.vision-to-values",
                defaultMessage:
                    "Define your company vision so you can form actionable business strategies."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/vision-to-values"
            )
        },
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.annual-plan-one-pager",
                defaultMessage: "annual plan one-pager"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:4da34e40-a8b8-45a5-8826-e0e08d24aeba/annual-plan-one-pager.svg?cdnVersion=1288",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.annual-plan-one-pager",
                defaultMessage: "Mike Cannon-Brookes Co-founder & CEO Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.annual-plan-one-pager",
                defaultMessage: "Annual plan one-pager"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.annual-plan-one-pager",
                defaultMessage:
                    "Share your company's annual plans and priorities across your entire organization."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/annual-plan-one-pager"
            )
        }
    ];

    const projectPlanningCardContentFirstRow: TemplatesTabCardContentProps[] = [
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.project-plan",
                defaultMessage: "project plan"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:438d81bd-fb9b-4711-ae6d-09b51690199b/project-plan.svg?cdnVersion=1282",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.project-plan",
                defaultMessage: "Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.project-plan",
                defaultMessage: "Project plan"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.project-plan",
                defaultMessage:
                    "Define, scope, and plan milestones for your next project."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/project-plan"
            )
        },
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.brainstorming",
                defaultMessage: "brainstorming"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:89b4a632-75c6-4476-b9d7-c65b03ad6baf/project-poster.svg?cdnVersion=1282",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.brainstorming",
                defaultMessage: "Mural"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.brainstorming",
                defaultMessage: "Brainstorming"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.brainstorming",
                defaultMessage:
                    "Plan, run, and document a remote brainstorming session for your next great idea."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/brainstorming"
            )
        }
    ];
    const projectPlanningCardContentSecondRow: TemplatesTabCardContentProps[] =
        [
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.retrospective",
                    defaultMessage: "retrospective"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:46179faa-d522-4456-84aa-5506c421b7cb/retrospective.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.retrospective",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.retrospective",
                    defaultMessage: "Retrospective"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.retrospective",
                    defaultMessage:
                        "What went well? What could have gone better? Crowdsource improvements with your team."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/retrospective"
                )
            },
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.master-project-documentation",
                    defaultMessage: "master project documentation"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:b2510ff2-88f8-449a-8c0b-565f09bc6dd0/master-project-documentation.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.master-project-documentation",
                    defaultMessage: "Mural"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.master-project-documentation",
                    defaultMessage: "Master project documentation"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.master-project-documentation",
                    defaultMessage:
                        "Create a master project doc to give stakeholders a single source of truth for your project."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/master-project-documentation"
                )
            }
        ];

    const softwareDevITCardContentFirstRow: TemplatesTabCardContentProps[] = [
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.devops-runbook",
                defaultMessage: "devOps runbook"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:d2183996-b8fa-4e9d-b884-fe359992fffa/devops-runbook.svg?cdnVersion=1282",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.devops-runbook",
                defaultMessage: "Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.devops-runbook",
                defaultMessage: "DevOps runbook"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.devops-runbook",
                defaultMessage:
                    "Prepare your operations team to quickly respond to system alerts and outages."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/devops-runbook"
            )
        },
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.experiment-plan-and-results",
                defaultMessage: "experiment plan and results"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:f85ade28-3e93-4fd0-b2a5-7db53c0cc3e0/experiment-plan-and-results.svg?cdnVersion=1282",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.experiment-plan-and-results",
                defaultMessage: "Optimizely"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.experiment-plan-and-results",
                defaultMessage: "Experiment plan and results"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.experiment-plan-and-results",
                defaultMessage:
                    "Document the details of your experiment including your hypothesis, variations, and results."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/experiment-plan-and-results"
            )
        }
    ];
    const softwareDevITCardContentSecondRow: TemplatesTabCardContentProps[] = [
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.incident-communication",
                defaultMessage: "incident communication"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:19d33f54-63ab-4a54-a653-094750746930/incident-communication.svg?cdnVersion=1282",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.incident-communication",
                defaultMessage: "Statuspage"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.incident-communication",
                defaultMessage: "Incident communication"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.incident-communication",
                defaultMessage:
                    "Use this template to create a guide for how to respond when things go wrong."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/incident-communication"
            )
        },
        {
            iconAlt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.how-to-article",
                defaultMessage: "how-to article"
            }),
            iconSrc:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:4d311a9a-cfd2-4dd3-a0c3-e6997ef71864/how-to-article.svg?cdnVersion=1282",
            upperText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.how-to-article",
                defaultMessage: "Atlassian"
            }),
            cardTitle: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.how-to-article",
                defaultMessage: "How-to article"
            }),
            text: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.how-to-article",
                defaultMessage:
                    "Provide step-by-step guidance for completing a task."
            }),
            href: useLocalizedHref(
                "/software/confluence/templates/how-to-article"
            )
        }
    ];

    const productManagementCardContentFirstRow: TemplatesTabCardContentProps[] =
        [
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.product-requirements",
                    defaultMessage: "product requirements"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:d476a381-c636-4daa-93d5-f0635500eaef/product-requirements.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.product-requirements",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.product-requirements",
                    defaultMessage: "Product requirements"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.product-requirements",
                    defaultMessage:
                        "Define, track and scope requirements for your product or feature."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/product-requirements"
                )
            },
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.product-roadmap",
                    defaultMessage: "product roadmap"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:9949ec6d-017d-4aa2-84ef-1f681323a6f5/product-roadmap.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.product-roadmap.",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.product-roadmap",
                    defaultMessage: "Product roadmap"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.product-roadmap",
                    defaultMessage:
                        "Capture your team’s high level product roadmap."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/product-roadmap"
                )
            }
        ];
    const productManagementCardContentSecondRow: TemplatesTabCardContentProps[] =
        [
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.product-launch",
                    defaultMessage: "product launch"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:123464b7-4a05-4c92-96f8-eaddb8aeef94/product-launch.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.product-launch.",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.product-launch",
                    defaultMessage: "Product launch"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.product-launch",
                    defaultMessage:
                        "Prepare your product launch, document strategy, and plan launch activities."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/product-launch"
                )
            },
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.premortem",
                    defaultMessage: "premortem"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:35784533-cbbf-49bd-8b58-a4495d1767b1/premortem.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.premortem",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.premortem",
                    defaultMessage: "Premortem"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.premortem",
                    defaultMessage:
                        "Use this template to run a pre-mortem session for your team to identify and plan for risks."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/premortem"
                )
            }
        ];

    const marketingAndSalesCardContentFirstRow: TemplatesTabCardContentProps[] =
        [
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.competitive-analysis",
                    defaultMessage: "competitive analysis"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:8e1754cb-a71c-4ff2-bfd8-5a76c7732a71/competitive-analysis.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.competitive-analysis",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.competitive-analysis",
                    defaultMessage: "Competitive analysis"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.competitive-analysis",
                    defaultMessage:
                        "Keep up with competitors by documenting their offerings and strategies."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/competitive-analysis"
                )
            },
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.marketing-campaign",
                    defaultMessage: "marketing campaign"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:78246c8d-48c1-4802-89a6-f583d98f8c1e/marketing-campaign.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.marketing-campaign",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.marketing-campaign",
                    defaultMessage: "Marketing campaign"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.marketing-campaign",
                    defaultMessage:
                        "Plan and track your marketing campaign tasks and deadlines through to execution."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/marketing-campaign"
                )
            }
        ];
    const marketingAndSalesCardContentSecondRow: TemplatesTabCardContentProps[] =
        [
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.content-strategy",
                    defaultMessage: "content strategy"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:c32f62ff-cebc-4035-b803-e2b19c55de7d/content-strategy.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.content-strategy",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.content-strategy",
                    defaultMessage: "Content strategy"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.content-strategy",
                    defaultMessage:
                        "Create a content strategy plan and organize your editorial calendar."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/content-strategy"
                )
            },
            {
                iconAlt: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.icon.sales-account-planning",
                    defaultMessage: "sales account planning"
                }),
                iconSrc:
                    "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:205911d6-f17a-4800-aaae-6c49b199d5a9/sales-account-planning.svg?cdnVersion=1282",
                upperText: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.upper-text.sales-account-planning",
                    defaultMessage: "Atlassian"
                }),
                cardTitle: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.title.sales-account-planning",
                    defaultMessage: "Sales account planning"
                }),
                text: intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.TemplatesSection.card.text.sales-account-planning",
                    defaultMessage:
                        "Develop a unique sales strategy for your accounts to close more deals."
                }),
                href: useLocalizedHref(
                    "/software/confluence/templates/sales-account-planning"
                )
            }
        ];

    const linkDataEventProps = (
        dataLabel: string,
        dataLabelEnglish: string
    ) => {
        const props: DataEventProps = {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "templatesSection",
            "data-event": "clicked",
            "data-label": dataLabel,
            "data-label-english": dataLabelEnglish
        };

        return props;
    };

    const tabs: TabsTypes[] = [
        {
            textLabel: tabOptions[0],
            id: "tab-featured",
            component: (
                <div css={fadeInStyles}>
                    <div css={outerGridStyles}>
                        <div css={innerGridStyles}>
                            {featuredCardContentFirstRow.map((cardProps) => (
                                <TemplatesTabCardContent
                                    {...cardProps}
                                    key={cardProps.href}
                                />
                            ))}
                        </div>
                        <div css={innerGridStyles}>
                            {featuredCardContentSecondRow.map((cardProps) => (
                                <TemplatesTabCardContent
                                    {...cardProps}
                                    key={cardProps.href}
                                />
                            ))}
                        </div>
                    </div>
                    <div css={tabLinkStyles}>
                        <Link
                            text={translatedTemplateTabLinks[0]}
                            href={useLocalizedHref(
                                "/software/confluence/templates/categories/featured"
                            )}
                            dataEventProps={linkDataEventProps(
                                translatedTemplateTabLinks[0],
                                "See all Featured templates"
                            )}
                            type="basic-underline"
                            customLinkColor="b400"
                            doesArrowAnimate={true}
                        />
                    </div>
                </div>
            )
        },
        {
            textLabel: tabOptions[1],
            id: "tab-project-planning",
            component: (
                <div css={fadeInStyles}>
                    <div css={outerGridStyles}>
                        <div css={innerGridStyles}>
                            {projectPlanningCardContentFirstRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                        <div css={innerGridStyles}>
                            {projectPlanningCardContentSecondRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <div css={tabLinkStyles}>
                        <Link
                            text={translatedTemplateTabLinks[1]}
                            href={useLocalizedHref(
                                "/software/confluence/templates/categories/project-planning"
                            )}
                            dataEventProps={linkDataEventProps(
                                translatedTemplateTabLinks[1],
                                "See all Project Planning templates"
                            )}
                            type="basic-underline"
                            customLinkColor="b400"
                            doesArrowAnimate={true}
                        />
                    </div>
                </div>
            )
        },
        {
            textLabel: tabOptions[2],
            id: "tab-software-development-it",
            component: (
                <div css={fadeInStyles}>
                    <div css={outerGridStyles}>
                        <div css={innerGridStyles}>
                            {softwareDevITCardContentFirstRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                        <div css={innerGridStyles}>
                            {softwareDevITCardContentSecondRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <div css={tabLinkStyles}>
                        <Link
                            text={translatedTemplateTabLinks[2]}
                            href={useLocalizedHref(
                                "/software/confluence/templates/categories/software-development"
                            )}
                            dataEventProps={linkDataEventProps(
                                translatedTemplateTabLinks[2],
                                "See all Software Development/IT templates"
                            )}
                            type="basic-underline"
                            customLinkColor="b400"
                            doesArrowAnimate={true}
                        />
                    </div>
                </div>
            )
        },
        {
            textLabel: tabOptions[3],
            id: "tab-product-mgmt",
            component: (
                <div css={fadeInStyles}>
                    <div css={outerGridStyles}>
                        <div css={innerGridStyles}>
                            {productManagementCardContentFirstRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                        <div css={innerGridStyles}>
                            {productManagementCardContentSecondRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <div css={tabLinkStyles}>
                        <Link
                            text={translatedTemplateTabLinks[3]}
                            href={useLocalizedHref(
                                "/software/confluence/templates/categories/product-management"
                            )}
                            dataEventProps={linkDataEventProps(
                                translatedTemplateTabLinks[3],
                                "See all Product Management templates"
                            )}
                            type="basic-underline"
                            customLinkColor="b400"
                            doesArrowAnimate={true}
                        />
                    </div>
                </div>
            )
        },
        {
            textLabel: tabOptions[4],
            id: "tab-marketing-sales",
            component: (
                <div css={fadeInStyles}>
                    <div css={outerGridStyles}>
                        <div css={innerGridStyles}>
                            {marketingAndSalesCardContentFirstRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                        <div css={innerGridStyles}>
                            {marketingAndSalesCardContentSecondRow.map(
                                (cardProps) => (
                                    <TemplatesTabCardContent
                                        {...cardProps}
                                        key={cardProps.href}
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <div css={tabLinkStyles}>
                        <Link
                            text={translatedTemplateTabLinks[4]}
                            href={useLocalizedHref(
                                "/software/confluence/templates/categories/marketing-and-sales"
                            )}
                            dataEventProps={linkDataEventProps(
                                translatedTemplateTabLinks[4],
                                "See all Marketing & Sales templates"
                            )}
                            type="basic-underline"
                            customLinkColor="b400"
                            doesArrowAnimate={true}
                        />
                    </div>
                </div>
            )
        }
    ];

    return (
        <section css={pageSectionStyles}>
            <div css={componentHeadingStyles}>
                <h2>
                    <FormattedMessage
                        id="confluence.section.templates.header"
                        defaultMessage="There's a template for that"
                    />
                </h2>
                <p>
                    <FormattedMessage
                        id="confluence.section.templates.text"
                        defaultMessage="From marketing and sales to development and operations, we
                        have templates for every type of team and project to get
                        your team doing the real work right away."
                    />
                </p>
                <Link
                    text={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.TemplatesSection.tab.projectmgmt",
                        defaultMessage: "See all templates"
                    })}
                    href={useLocalizedHref("/software/confluence/templates")}
                    dataEventProps={{
                        "data-product-key": "confluence",
                        "data-event-component": "linkButton",
                        "data-event-container": "templatesSection",
                        "data-event": "clicked"
                    }}
                    type="basic-underline"
                    customLinkColor="b400"
                    doesArrowAnimate={true}
                />
            </div>
            <div css={tabContainerStyles}>
                <TabGroup
                    tabContentType="custom"
                    tabs={tabs}
                    id="confluence-templates"
                    label="confluence-templates"
                    colors={tabStyles}
                    dataProductKey="confluence"
                    shouldUseRowRevised={true}
                />
            </div>
        </section>
    );
};

export default TemplatesSection;
